/**
 * 💜 SPACE HEARTBEATS - Module Heartbeats Stats
 * Statistiques de la communauté de rencontres
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, Users, MessageCircle, Gamepad2, Trophy, Eye,
  TrendingUp, Star, Shield, Video, Flame, Sparkles,
  Loader2, RefreshCw, BarChart3, Crown
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import HeartbeatsDashboard from './HeartbeatsDashboard';

// Carte stat holographique rose/violet
const HeartbeatStat = ({ icon: Icon, title, value, subtitle, color = 'pink' }) => {
  const colorMap = {
    pink: 'border-pink-500/30 bg-pink-500/10 text-pink-400',
    purple: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
    red: 'border-red-500/30 bg-red-500/10 text-red-400',
    cyan: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
    yellow: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400',
    green: 'border-green-500/30 bg-green-500/10 text-green-400',
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-xl rounded-2xl p-5 border ${colorMap[color]}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{title}</p>
          <p className={`text-3xl font-black ${colorMap[color].split(' ')[2]}`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtitle && <p className="text-gray-600 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl border ${colorMap[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </motion.div>
  );
};

// Carte joueur du leaderboard
const LeaderboardCard = ({ player, rank, type }) => {
  const rankColors = {
    1: 'from-yellow-500 to-amber-600',
    2: 'from-gray-400 to-gray-500',
    3: 'from-amber-600 to-orange-700',
  };
  const rankIcons = { 1: '🥇', 2: '🥈', 3: '🥉' };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.1 }}
      className="flex items-center gap-4 p-3 rounded-xl bg-white/5 border border-white/10"
    >
      {/* Rang */}
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white ${
        rank <= 3 ? `bg-gradient-to-br ${rankColors[rank]}` : 'bg-gray-700'
      }`}>
        {rankIcons[rank] || rank}
      </div>

      {/* Joueur */}
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{player.name || player.customerId}</p>
        <p className="text-xs text-gray-500">{player.gamesPlayed || 0} parties jouées</p>
      </div>

      {/* Score */}
      <div className="text-right">
        <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">
          {player.totalScore?.toLocaleString() || player.score?.toLocaleString() || 0}
        </p>
        <p className="text-xs text-gray-500">points</p>
      </div>
    </motion.div>
  );
};

// Module Heartbeats principal
const SpaceHeartbeats = () => {
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Charger les données
  const loadData = async () => {
    setIsLoading(true);
    try {
      // Essayer de charger les stats depuis différentes sources
      let profileCount = 0;
      let matchCount = 0;
      let crushCount = 0;
      let gameScores = [];

      // Profils visibles
      try {
        const profiles = await api.getVisibleProfiles?.() || { profiles: [] };
        profileCount = profiles.profiles?.length || profiles.length || 0;
      } catch (e) { console.log('Pas de profils visibles'); }

      // Matchs et crushes via admin
      try {
        const adminStats = await api.getAdminHeartbeatsStats?.();
        if (adminStats) {
          matchCount = adminStats.matchCount || 0;
          crushCount = adminStats.crushCount || 0;
        }
      } catch (e) { console.log('Pas de stats admin'); }

      // Leaderboard jeux
      try {
        const lb = await api.getGamesLeaderboard?.() || [];
        gameScores = Array.isArray(lb) ? lb : lb.leaderboard || [];
      } catch (e) { console.log('Pas de leaderboard'); }

      setStats({
        totalProfiles: profileCount,
        matchCount,
        crushCount,
        gamePlayers: gameScores.length,
        totalGameScore: gameScores.reduce((sum, p) => sum + (p.totalScore || p.score || 0), 0),
      });
      setLeaderboard(gameScores.slice(0, 10));
    } catch (error) {
      console.error('Erreur chargement stats Heartbeats:', error);
      setStats({
        totalProfiles: 0,
        matchCount: 0,
        crushCount: 0,
        gamePlayers: 0,
        totalGameScore: 0,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Heart className="w-12 h-12 text-pink-500 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500">Chargement des données Heartbeats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-500 to-pink-400">
            💜 HEARTBEATS
          </h1>
          <p className="text-gray-500 text-sm">Statistiques de la communauté de rencontres</p>
        </div>
        <Button
          onClick={loadData}
          className="bg-pink-500/20 text-pink-400 hover:bg-pink-500/30"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats principales */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <HeartbeatStat
          icon={Users}
          title="Profils Actifs"
          value={stats?.totalProfiles || 0}
          subtitle="Membres inscrits"
          color="pink"
        />
        <HeartbeatStat
          icon={Heart}
          title="Matchs"
          value={stats?.matchCount || 0}
          subtitle="Connexions mutuelles"
          color="red"
        />
        <HeartbeatStat
          icon={Flame}
          title="Crushes"
          value={stats?.crushCount || 0}
          subtitle="Likes envoyés"
          color="purple"
        />
        <HeartbeatStat
          icon={Gamepad2}
          title="Joueurs"
          value={stats?.gamePlayers || 0}
          subtitle="Participants aux jeux"
          color="cyan"
        />
        <HeartbeatStat
          icon={Trophy}
          title="Points Totaux"
          value={stats?.totalGameScore || 0}
          subtitle="Score cumulé"
          color="yellow"
        />
      </div>

      {/* Sections détaillées */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Leaderboard des jeux */}
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-purple-500/20 bg-[#12121a]/60">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            🏆 Top Joueurs
          </h3>
          {leaderboard.length === 0 ? (
            <div className="text-center py-8">
              <Gamepad2 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Aucun joueur pour le moment</p>
              <p className="text-gray-600 text-xs mt-1">Les scores apparaîtront après les premières parties</p>
            </div>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((player, idx) => (
                <LeaderboardCard key={idx} player={player} rank={idx + 1} type="games" />
              ))}
            </div>
          )}
        </div>

        {/* Activité Heartbeats */}
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-pink-500/20 bg-[#12121a]/60">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-pink-400" />
            📊 Activité
          </h3>
          
          <div className="space-y-4">
            {/* Profils avec vidéo */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
              <div className="flex items-center gap-3">
                <Video className="w-5 h-5 text-purple-400" />
                <span className="text-white">Profils avec vidéo</span>
              </div>
              <span className="text-purple-400 font-bold">À venir</span>
            </div>

            {/* Profils vérifiés */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-green-500/10 border border-green-500/20">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-white">Profils vérifiés</span>
              </div>
              <span className="text-green-400 font-bold">À venir</span>
            </div>

            {/* Quiz Psyche complétés */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                <span className="text-white">Quiz Psyche™ complétés</span>
              </div>
              <span className="text-cyan-400 font-bold">À venir</span>
            </div>
          </div>

          {/* Message encourageant */}
          <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20">
            <p className="text-sm text-gray-300">
              <Star className="w-4 h-4 inline text-yellow-400 mr-1" />
              <strong className="text-pink-400">Astuce :</strong> Plus il y a de profils actifs, 
              plus la communauté est vivante ! Encouragez vos clients à créer leur profil Heartbeats.
            </p>
          </div>
        </div>
      </div>

      {/* 💜 Dashboard Autorépondeur Heartbeats */}
      <HeartbeatsDashboard />
    </div>
  );
};

export default SpaceHeartbeats;
